import React, { useEffect, useRef, useState } from 'react';
import {
    InvoiceByCarrierNo,
    InvoiceByCarrierType,
    InvoiceByDonate,
    InvoiceByIssuingType,
    InvoiceByManual,
    InvoiceByNotes,
    InvoiceByTriplicateUniform,
    InvoiceNotificationCellPhone,
    InvoiceNotificationEmail,
    useInvoiceFieldVisibility,
} from '@Local/Components/Invoice';
import { isEmpty, isFunction, localUser, refIsRequiredError } from '@Util/utils';
import { Alert, Button, Card, CheckboxField, Divider, TextField } from '@curves/ui-base';

import { Grid } from '@mui/material';
import useOrderApi from '@Apis/useOrderApi';
const OrderInvoiceReissuingForm = ({ orderID, sourceData = {}, refresh }) => {
    const {
        issuingType: defaultIssuingType = 2,
        invoiceCarrierType: defaultInvoiceCarrierType = 1,
        donationCode,
        invoiceNo,
        invoiceCarrierNo,
        issuingEmail,
        issuingCellPhone,
        invoiceNotes,
        invoiceCarrierTypeOptions = [],
        isIssued,
        tipText,
        invoiceExceptionMessage,
    } = sourceData || {};
    const { userID } = localUser.get();
    const { postOrdersReissuingApi } = useOrderApi();
    // 發票相關狀態
    const [issuingType, setIssuingType] = useState(defaultIssuingType);
    const [invoiceCarrierType, setInvoiceCarrierType] = useState(defaultInvoiceCarrierType);
    // 使用 useInvoiceFieldVisibility hook 管理欄位顯示邏輯
    const fieldVisibility = useInvoiceFieldVisibility({
        issuingType,
        invoiceCarrierType,
    });
    // 發票元件 refs
    const invoiceByIssuingTypeRef = useRef(null);
    const invoiceByDonateRef = useRef(null);
    const invoiceByTriplicateUniformRef = useRef(null);
    const invoiceByManualRef = useRef(null);
    const invoiceByCarrierTypeRef = useRef(null);
    const invoiceByCarrierNoRef = useRef(null);
    const invoiceNotificationEmailRef = useRef(null);
    const invoiceNotificationPhoneRef = useRef(null);
    const invoiceByNotesRef = useRef(null);
    // 其他元件 refs
    const notesRef = useRef(null);
    const isConfirmRef = useRef(null);
    // 處理發票類型變更
    const handleIssuingTypeChange = (value) => {
        setIssuingType(value);
        // 當發票類型變更時，重置載具類型為預設值
        if (value === 2) {
            // 二聯式發票
            setInvoiceCarrierType(1);
        }
    };
    // 處理載具類型變更
    const handleInvoiceCarrierTypeChange = (value) => {
        setInvoiceCarrierType(value);
    };
    const doPostOrdersReissuingApi = async (params) => {
        try {
            const resp = await postOrdersReissuingApi(params);
            if (resp) {
                isFunction(refresh) && refresh();
            }
        } catch (error) {
            console.error('發票重開失敗:', error);
        }
    };
    // 取得所有表單結果
    const getResult = () => {
        let result = {};
        // 發票類型
        if (invoiceByIssuingTypeRef.current?.getResult) {
            result = { ...result, ...invoiceByIssuingTypeRef.current.getResult() };
        }
        // 捐贈發票
        if (fieldVisibility.showDonateField && invoiceByDonateRef.current?.getResult) {
            result = { ...result, ...invoiceByDonateRef.current.getResult() };
        }
        // 三聯式發票
        if (fieldVisibility.showTriplicateUniformField && invoiceByTriplicateUniformRef.current?.getResult) {
            result = { ...result, ...invoiceByTriplicateUniformRef.current.getResult() };
        }
        // 手開發票
        if (fieldVisibility.showManualInvoiceField && invoiceByManualRef.current?.getResult) {
            result = { ...result, ...invoiceByManualRef.current.getResult() };
        }
        // 載具類型
        if (fieldVisibility.showCarrierTypeField && invoiceByCarrierTypeRef.current?.getResult) {
            const { carrierType } = invoiceByCarrierTypeRef.current.getResult();
            result = { ...result, invoiceCarrierType: carrierType };
        }
        // 載具號碼
        if (fieldVisibility.showCarrierNoField && invoiceByCarrierNoRef.current?.getResult) {
            const { carrierNo } = invoiceByCarrierNoRef.current.getResult();
            result = { ...result, invoiceCarrierNo: carrierNo };
        }
        // 發票通知信箱
        if (invoiceNotificationEmailRef.current?.getResult) {
            result = { ...result, ...invoiceNotificationEmailRef.current.getResult() };
        }
        // 發票通知手機號碼
        if (invoiceNotificationPhoneRef.current?.getResult) {
            result = { ...result, ...invoiceNotificationPhoneRef.current.getResult() };
        }
        // 發票備註
        if (invoiceByNotesRef.current?.getResult) {
            result = { ...result, ...invoiceByNotesRef.current.getResult() };
        }
        // 一般備註
        if (notesRef.current?.getResult) {
            result.notes = notesRef.current.getResult();
        }
        return result;
    };
    // 檢查是否有錯誤
    const isError = () => {
        const refs = [
            invoiceByIssuingTypeRef,
            fieldVisibility.showDonateField && invoiceByDonateRef,
            fieldVisibility.showTriplicateUniformField && invoiceByTriplicateUniformRef,
            fieldVisibility.showManualInvoiceField && invoiceByManualRef,
            fieldVisibility.showCarrierTypeField && invoiceByCarrierTypeRef,
            fieldVisibility.showCarrierNoField && invoiceByCarrierNoRef,
            invoiceNotificationEmailRef,
            invoiceNotificationPhoneRef,
            invoiceByNotesRef,
            isConfirmRef,
        ].filter(Boolean);
        return refIsRequiredError(...refs);
    };
    const handleSubmit = () => {
        if (!isError()) {
            const params = {
                employeeID: userID || 0,
                orderID: orderID || 0,
                isIssued: sourceData?.isIssued ?? false,
                invoiceNo: sourceData?.invoiceNo ?? '',
                ...getResult(),
            };
            doPostOrdersReissuingApi(params);
        }
    };
    const alertStyles = isIssued ? 'warning' : 'error';
    useEffect(
        () => {
            if (defaultIssuingType) {
                setIssuingType(defaultIssuingType);
            }
        },
        // eslint-disable-next-line
        [defaultIssuingType],
    );
    useEffect(
        () => {
            if (defaultInvoiceCarrierType) {
                setInvoiceCarrierType(defaultInvoiceCarrierType);
            }
        },
        // eslint-disable-next-line
        [defaultInvoiceCarrierType],
    );
    if (isEmpty(sourceData)) return null;
    return (
        <>
            <Card className="mb-2">
                <Card.CardHeader title={'發票設定'} />
                <Card.CardContent className="px-2 pb-3 pt-0">
                    <Grid spacing={2} container>
                        {/* 提示訊息 */}
                        <Grid xs={12} item>
                            <Alert severity={alertStyles} variant="outlined" color={alertStyles}>
                                {isIssued ? tipText : invoiceExceptionMessage}
                            </Alert>
                        </Grid>
                        {/* 發票類型選擇 */}
                        <Grid xs={12} item>
                            <InvoiceByIssuingType ref={invoiceByIssuingTypeRef} issuingType={issuingType} onChange={handleIssuingTypeChange} />
                        </Grid>
                        {/* 捐贈發票 */}
                        {fieldVisibility.showDonateField && (
                            <Grid xs={12} item>
                                <InvoiceByDonate ref={invoiceByDonateRef} donationCode={donationCode} />
                            </Grid>
                        )}
                        {/* 三聯式發票 */}
                        {fieldVisibility.showTriplicateUniformField && (
                            <Grid xs={12} item>
                                <InvoiceByTriplicateUniform ref={invoiceByTriplicateUniformRef} taxID={sourceData?.taxID} taxTitle={sourceData?.taxTitle} />
                            </Grid>
                        )}
                        {/* 手開發票 */}
                        {fieldVisibility.showManualInvoiceField && (
                            <Grid xs={12} item>
                                <InvoiceByManual ref={invoiceByManualRef} invoiceNo={invoiceNo} />
                            </Grid>
                        )}
                        {/* 載具類型 */}
                        {fieldVisibility.showCarrierTypeField && (
                            <Grid xs={12} item>
                                <InvoiceByCarrierType
                                    ref={invoiceByCarrierTypeRef}
                                    carrierType={invoiceCarrierType}
                                    options={invoiceCarrierTypeOptions}
                                    onChange={handleInvoiceCarrierTypeChange}
                                />
                            </Grid>
                        )}
                        {/* 載具號碼 */}
                        {fieldVisibility.showCarrierNoField && (
                            <Grid xs={12} item>
                                <InvoiceByCarrierNo ref={invoiceByCarrierNoRef} carrierNo={invoiceCarrierNo} carrierType={invoiceCarrierType} />
                            </Grid>
                        )}
                        {/* 發票備註 */}
                        {fieldVisibility.showInvoiceNotesField && (
                            <Grid xs={12} item>
                                <InvoiceByNotes ref={invoiceByNotesRef} invoiceNotes={invoiceNotes} />
                            </Grid>
                        )}
                    </Grid>
                </Card.CardContent>
            </Card>
            <Card>
                <Card.CardHeader title={'發票通知設定'} />
                <Card.CardContent className="px-2 pb-3 pt-0">
                    <Grid spacing={2} container>
                        {/* 發票通知信箱 */}
                        <Grid xs={6} item>
                            <InvoiceNotificationEmail label={'Email'} ref={invoiceNotificationEmailRef} defaultValue={issuingEmail} required />
                        </Grid>
                        {/* 發票通知手機號碼 */}
                        <Grid xs={6} item>
                            <InvoiceNotificationCellPhone label={'手機號碼'} ref={invoiceNotificationPhoneRef} defaultValue={issuingCellPhone} required />
                        </Grid>
                        <Grid xs={12} item>
                            <Divider />
                        </Grid>
                        {/* 一般備註 */}
                        <Grid xs={12} item>
                            <TextField ref={notesRef} label="重開原因" rows={2} multiline fullWidth required={isIssued} />
                        </Grid>
                        {/* 確認勾選 */}
                        <Grid xs={12} item>
                            <CheckboxField ref={isConfirmRef} required>
                                <CheckboxField.Checkbox value={true} label="我了解此設定僅影響本訂單，如需同步會員資料請聯絡店鋪教練" />
                            </CheckboxField>
                        </Grid>
                        <Grid xs={12} item>
                            <Divider className={'mb-4'} />
                        </Grid>
                        <Grid className={'text-right'} xs={12} item>
                            <Button variant="contained" color="primary" onClick={handleSubmit}>
                                確認送出
                            </Button>
                        </Grid>
                    </Grid>
                </Card.CardContent>
            </Card>
        </>
    );
};
export default OrderInvoiceReissuingForm;
